/**
 * @file index.tsx
 *
 * @fileoverview The context that stores and updates bookmarked resumes.
 */

import { createContext, Dispatch, SetStateAction, useState } from 'react';
import { useIntl } from 'react-intl';
import {
  useBookmarkedResumes,
  UseBookmarkedResumesInterface,
} from '~/jobiqo/components/BookmarkedResumes/hooks/useBookmarkedResumes';
import { useFlags } from '~/jobiqo/hooks/flags';
import { BookmarkResponse, Resume } from '~/jobiqo/types/types';

const BOOKMARKED_RESUMES_LIMIT = 500;
export interface BookmarkedResumesContextInterface
  extends UseBookmarkedResumesInterface {
  /**
   * The current update bookmark repsonse.
   */
  updateBookmarkResponse?: BookmarkResponse;
  /**
   * Returns the bookmarked resume by id.
   * Initially, this is undefined and only gets defined once the context mounts.
   * That's why we can use this to check if the context did mount.
   */
  getBookmarkedResume(id: number): Resume | undefined;
  /**
   * The function to set update bookmark response data.
   */
  setUpdateBoomarkResponse: Dispatch<
    SetStateAction<BookmarkResponse | undefined>
  >;
  /**
   * The display bookmark update flag function.
   */
  dispatchBookmarkUpdateFlag(): void;
}

export const BookmarkedResumesContext = createContext<
  BookmarkedResumesContextInterface
>({
  bookmarkedResumes: [],
  errors: [],
  error: false,
  loading: false,
  total: 0,
  getBookmarkedResume: () => undefined,
  setBookmarkedResumes: () => {},
  setUpdateBoomarkResponse: () => {},
  dispatchBookmarkUpdateFlag: () => {},
});

function BookmarkedResumesProvider({
  children,
  queryParams = {
    itemsPerPage: BOOKMARKED_RESUMES_LIMIT,
    page: 0,
  },
  initialState,
}) {
  const {
    bookmarkedResumes,
    setBookmarkedResumes,
    ...hookReturnValues
  } = useBookmarkedResumes(queryParams, initialState);
  const flagService = useFlags();
  const [updateBookmarkResponse, setUpdateBoomarkResponse] = useState<
    BookmarkResponse
  >();
  const intl = useIntl();

  function dispatchBookmarkUpdateFlag() {
    if (updateBookmarkResponse) {
      !!updateBookmarkResponse.errors?.length
        ? flagService.addFlag({
            type: 'danger',
            text: intl.formatMessage({
              id: 'resume_bookmark_error',
              defaultMessage:
                'An error happened while updating your resume bookmark.',
            }),
          })
        : flagService.addFlag({
            type: 'success',
            text: updateBookmarkResponse.isBookmarked
              ? intl.formatMessage({
                  id: 'resume_bookmark_flag_success_bookmark',
                  defaultMessage: 'Resume successfully bookmarked',
                })
              : intl.formatMessage({
                  id: 'resume_bookmark_flag_success_unbookmark',
                  defaultMessage: 'Resume successfully unbookmarked',
                }),
          });
    }
  }

  return (
    <BookmarkedResumesContext.Provider
      value={{
        bookmarkedResumes,
        setBookmarkedResumes,
        ...hookReturnValues,
        setUpdateBoomarkResponse,
        updateBookmarkResponse,
        dispatchBookmarkUpdateFlag,
        getBookmarkedResume: (id) =>
          bookmarkedResumes.find((resume) => resume.id === id),
      }}
    >
      {children}
    </BookmarkedResumesContext.Provider>
  );
}

export default BookmarkedResumesProvider;
